import './defines';
import ProductState from 'classes/productstate';
import DialogView from 'components/dialogs';
import EventBus from 'components/event-bus';
import analytics from 'controllers/analytics';
import auth from 'controllers/auth';
import connector from 'controllers/connector';
import navigate from 'controllers/navigate';
import { RouteOptions } from 'interfaces/app';
import deleteProduct from 'mutations/product/delete';
import URL_PARAMETERS from 'settings/url-parameters';
import {
	AppStateModule,
	ChannelsModule,
	ConfigModule,
	ProductsModule,
	ProductStateModule,
	UserModule,
} from 'store';
import _ from 'underscore';
import { Public } from 'utils/decorators';
import {
	Component,
	toNative,
} from 'utils/vue-facing-decorator';
import SupportView from 'views/support';
import WebPushSoftAskView from 'views/web-push-soft-ask/template.vue';
import { Vue } from 'vue-facing-decorator';
import type { NavigationFailure } from 'vue-router';
import { routerStateHandler } from './helpers';
import Template from './template.vue';

export {
	configureRouter,
	enableRouting,
} from './helpers';

@Component({
	components: {
		DialogView,
		WebPushSoftAskView,
		SupportView,
	},
	mixins: [Template],
})
class AppRouterView extends Vue {
	protected get showSupport() {
		return (
			ConfigModule.partnerID === 2
			&& AppStateModule.showSupportButton
		);
	}

	protected get webPushAvailable() {
		return AppStateModule.webPushAvailable;
	}

	protected componentKey = 0;

	public loading = true;

	protected created() {
		window.addEventListener(
			'online',
			this.setOnline,
		);
		window.addEventListener(
			'offline',
			this.setOffline,
		);

		if (this.$router) {
			this.$router.beforeEach((to, from, next) => {
				if (to.path !== from.path) {
					this.loading = true;
				}

				next();
			});
			this.$router.afterEach((to) => {
				/**
				 * We only want to hide the loading spinner if we have a matched route
				 * and routing is enabled.
				 * If the router is not yet enabled we know that the pending route
				 * will be navigated again to once the router is enabled.
				 */
				if (
					to.matched.length
					&& routerStateHandler.routingEnabled
				) {
					this.loading = false;
				}
			});
		} else {
			this.loading = false;
		}
	}

	@Public()
	public back(): Decorated<void> {
		this.$router.go(-1);
	}

	@Public()
	public navigate(
		route: string,
		routeOptions: RouteOptions,
	): Decorated<void> {
		routeOptions = _.extend(
			{
				replace: false,
			},
			routeOptions,
		);

		if (routeOptions.replace) {
			this.$router
				.replace(route)
				.then(routeOptions.onComplete);
		} else {
			this.$router
				.push(route)
				.then(routeOptions.onComplete);
		}
	}

	@Public()
	public openProduct(
		projectId: number | 'latest',
		replace: boolean,
		action?: 'extendProjectLifetime' | 'removeProject',
	): Decorated<void> {
		// This route is only available to logged in users
		if (!UserModule.id) {
			EventBus.once(
				'auth:login',
				(success: boolean) => {
					if (success) {
						this.openProduct(
							projectId,
							replace,
						);
					} else {
						navigate.toStart();
					}
				},
			);

			auth.showLogin({
				hasclose: false,
				showGuest: false,
				showGoogleLogin: !!(connector.isSupported('gplus') && ChannelsModule.getById('gplus')?.login),
			});
		} else if (projectId == 'latest') {
			ProductsModule
				.fetch({
					requestOptions: {
						params: {
							limit: 1,
							orderby: 'id DESC',
						},
					},
				})
				.then(() => {
					if (ProductsModule.models.length) {
						const productModel = ProductsModule.models[0];
						window.App.router.openProduct(
							productModel.id,
							true,
						);
					} else {
						navigate.toStart();
					}
				})
				.catch((err) => {
					this.$openErrorDialog({
						body: {
							content: err.message,
						},
					});
					navigate.toStart();
				});
		} else {
			// In case the user was deeplinked with custom attributes, we need to add these to the project data
			const customAttributes = (
				this.$route.query.hasOwnProperty(URL_PARAMETERS.customAttributes)
					? JSON.parse(this.$route.query[URL_PARAMETERS.customAttributes] as string)
					: undefined
			);
			const url = new URL(window.location.href);
			url.searchParams.delete(URL_PARAMETERS.customAttributes);

			ProductState
				.setup(
					projectId,
					{
						customAttributes,
						extendProjectLifetime: action && action === 'extendProjectLifetime',
					},
				)
				// Navigate into the project
				.then(() => {
					navigate.openProduct(
						projectId,
						{
							trigger: true,
							replace,
						},
						(
							url.searchParams.size > 0
								? {
									query: url.searchParams.toString(),
								}
								: undefined
						),
					);

					const productModel = ProductStateModule.getProduct;
					if (productModel) {
						analytics.trackOpenProject(productModel);
					}

					if (
						action
						&& action === 'extendProjectLifetime'
					) {
						const closeAlert = this.$openAlertDialog({
							header: {
								title: this.$t('dialogs.projectLifetimeExtended.title'),
							},
							body: {
								content: this.$t('dialogs.projectLifetimeExtended.message'),
							},
							footer: {
								buttons: [
									{
										id: 'accept',
										text: this.$t('dialogs.projectLifetimeExtended.buttons.accept'),
										click: () => {
											closeAlert();
										},
									},
								],
							},
						});
					} else if (
						action
						&& action === 'removeProject'
					) {
						// Show confirmation dialog
						const closeConfirm = this.$openConfirmDialog({
							header: {
								title: this.$t('dialogHeaderRemoveProduct'),
							},
							body: {
								content: this.$t('dialogTextRemoveProduct'),
							},
							footer: {
								buttons: [
									{
										id: 'cancel',
										text: this.$t('dialogButtonCancel'),
										click: () => {
											closeConfirm();
										},
									},
									{
										id: 'accept',
										text: this.$t('dialogButtonRemoveProductOk'),
										click: () => {
											if (ProductStateModule.getProduct) {
												deleteProduct(projectId)
													.then(() => {
														navigate.toStart();
														closeConfirm();
													})
													.catch(() => {
														this.reload();
													});
											} else {
												navigate.toStart();
												closeConfirm();
											}
										},
									},
								],
							},
						});
					}
				})
				.catch((err) => {
					let dialogErrorText = window.App.router.$t('dialogTextError');
					if (err.message) {
						dialogErrorText += `\n\nError: ${err.message}`;
					}

					this.$openErrorDialog({
						body: {
							content: dialogErrorText,
						},
					});

					navigate.toStart();
				});
		}
	}

	@Public()
	public reload(): Decorated<void> {
		// We might be in loading state
		this.loading = false;

		// We have all components rerendered by changing the key of the master element
		// This forces a rerender of all subcomponents (and translations)
		this.componentKey += 1;
	}

	@Public()
	public setLoading(loading: boolean): Decorated<void> {
		this.loading = loading;
	}

	@Public()
	public removeURLParameter(parameter: string): Decorated<Promise<NavigationFailure | void | undefined>> {
		/**
		 * If the route is not matched, this means that either the router has not yet the routing
		 * enabled or that there is no actually matching route, one case could be that this function
		 * is called in the `checkToken()` (@see {@link ./src/js/ops/launch.ts}), if there is no
		 * matching route then the `query` property of the `this.$route` object will be empty.
		 * So we need to instead remove the parameter from the URL, replace the state and then
		 * replace the current $route with a new object that has the query object updated.
		 */
		if (!this.$route.matched.length) {
			const url = new URL(window.location.href);
			url.searchParams.delete(parameter);
			window.history.replaceState(
				null,
				'',
				url.toString(),
			);

			return this.$router.replace({
				path: url.pathname.replace(
					this.$router.options.history.base,
					'',
				),
				query: Object.fromEntries(url.searchParams),
			}) as Decorated<Promise<NavigationFailure | void | undefined>>;
		}

		const query = { ...this.$route.query };
		delete query[parameter];

		return this.$router.replace({ query }) as Decorated<Promise<NavigationFailure | void | undefined>>;
	}

	private setOnline() {
		AppStateModule.setOnline();
	}

	private setOffline() {
		AppStateModule.setOffline();
	}
}

export default toNative(AppRouterView);
